import React from 'react';
import { TransactionItem } from '../../models/TransactionItem';
import { Button } from '../ui';
import { createCurrencyFormatter } from '../../utils/globalCurrency';
import { codeToSymbol } from '../../utils/currencyUtils';
import { useAppContext } from '../../contexts/Storage';

interface DeleteTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: TransactionItem | null;
  onConfirmDelete: (transactionId: number) => Promise<void>;
  isDeleting?: boolean;
}

const DeleteTransactionModal: React.FC<DeleteTransactionModalProps> = ({
  isOpen,
  onClose,
  transaction,
  onConfirmDelete,
  isDeleting = false,
}) => {
  const { selectedRecord } = useAppContext();
  
  const formatCurrency = (amount?: number, currency?: string) => {
    if (amount === undefined) return 'N/A';
    // Use transaction currency, fallback to selected record's currency, then default to '$'
    const currencyCode = currency || selectedRecord?.record_currency_code || '$';
    // Convert currency code to symbol if needed
    const currencySymbol = codeToSymbol(currencyCode);
    const formatter = createCurrencyFormatter(currencySymbol);
    return formatter.format(amount);
  };

  if (!isOpen || !transaction) return null;

  const handleDelete = async () => {
    if (!transaction?.id) return;
    await onConfirmDelete(transaction.id);
  };

  const transactionName =
    transaction.action?.transaction_entity_name && transaction.direct_object?.transaction_entity_name
      ? `${transaction.action.transaction_entity_name} - ${transaction.direct_object.transaction_entity_name}`
      : `Transaction #${transaction.getHashedId?.() || transaction.id}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-700 bg-opacity-50">
      <div className="bg-light rounded-lg shadow-lg max-w-md w-full mx-4 border-l-4 border-danger">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-2 text-danger">Delete Transaction</h3>

          <p className="text-grey_dark mb-4">
            Are you sure you want to delete &quot;{transactionName}&quot; ({formatCurrency(transaction.amount, transaction.currency)})?
            This action cannot be undone.
          </p>

          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="bordered-primary"
              onClick={onClose}
              className="min-w-[80px]"
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={isDeleting}
              className="min-w-[80px]"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteTransactionModal;
